import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import Overlay from '../europe/Overlay';
import convertState from '../convertState';
import ChampionGrid from './ChampionGrid';
import BOControlPanel from './BOControlPanel';
import ExportCard from './ExportCard';
import useChampionData from './useChampionData';
import useCoachDraft from './useCoachDraft';
import useCoachTimer from './useCoachTimer';
import useCoachBO from './useCoachBO';
import useExportTactic from './useExportTactic';
import useTournamentData from './useTournamentData';
import useBOArchive, { makeArchive } from './useBOArchive';
import BOArchivePanel from './BOArchivePanel';
import useAISettings from './ai/useAISettings';
import useAIInsights from './ai/useAIInsights';
import AISettingsPanel from './ai/AISettingsPanel';
import AIInsightPanel from './ai/AIInsightPanel';
import { DEFAULT_STEP_SECONDS } from './draftSequence';
import css from './CoachApp.module.scss';

const COACH_CONFIG = {
  frontend: {
    scoreEnabled: false,
    spellsEnabled: false,
    coachesEnabled: false,
    blueTeam: {
      name: '蓝方 BLUE',
      score: 0,
      coach: '',
      color: 'rgb(0,151,196)',
    },
    redTeam: {
      name: '红方 RED',
      score: 0,
      coach: '',
      color: 'rgb(222,40,70)',
    },
    patch: '',
  },
};

const CoachApp = () => {
  const { champions, loading, error } = useChampionData('zh_CN');
  const bo = useCoachBO();
  const tournamentData = useTournamentData();

  // BO 档案：localStorage 持久化 + JSON 导入导出
  const archive = useBOArchive();
  const [archivePanelOpen, setArchivePanelOpen] = useState(false);

  // 当前 BO 各局胜负记录（不持久化，存档时一起 dump 进去；下一 BO 重置）
  const [currentWinners, setCurrentWinners] = useState({}); // { gameIndex: 'blue'|'red' }
  const setGameWinnerLive = useCallback((gameIndex, winner) => {
    setCurrentWinners((prev) => ({ ...prev, [gameIndex]: winner }));
  }, []);
  // 双方队名（保存时用）
  const [currentBlueName, setCurrentBlueName] = useState('蓝方');
  const [currentRedName, setCurrentRedName]   = useState('红方');

  // 把 BO 全局锁交给 draft；draft 自己内部会和当前局的 used 合并
  const externalLockedIds = useMemo(() => {
    const s = new Set();
    bo.globalLockedMap.forEach((_, id) => s.add(id));
    return s;
  }, [bo.globalLockedMap]);

  const draft = useCoachDraft({
    firstSide: bo.currentFirstSide,
    externalLockedIds,
  });

  const timer = useCoachTimer({
    stepKey: `${bo.currentGameIndex}-${draft.stepIndex}`,
    stepSeconds: DEFAULT_STEP_SECONDS,
    isFinished: draft.isFinished,
  });

  // 进入下一局：把当前局的快照写入 BO 历史 → 推进到下一局序号 → 重置 draft & timer
  const onNextGame = () => {
    if (!draft.isFinished) return;
    if (bo.currentGameIndex + 1 > bo.totalGames) return;
    bo.recordGameAndAdvance(draft.snapshotPicks());
    draft.reset();
    timer.resetTimer();
  };

  // 切换 BO 模式 / 重置 BO：也得把 draft & timer 一并归零
  const onChangeMode = (newMode) => {
    bo.changeMode(newMode);
    draft.reset();
    timer.resetTimer();
    setCurrentWinners({}); // 新 BO 重置胜负
  };
  const onResetBO = () => {
    bo.resetBO();
    draft.reset();
    timer.resetTimer();
    setCurrentWinners({});
  };

  // ===== BO 档案：保存 / 加载 =====

  const canSaveCurrentBO = bo.completedGames.length > 0;

  const handleSaveCurrentBO = useCallback(() => {
    if (!canSaveCurrentBO) return;
    const data = bo.serializeCurrent();
    const winnersArr = [];
    Object.entries(currentWinners).forEach(([gi, w]) => { winnersArr[Number(gi)] = w; });
    const arc = makeArchive({
      mode: data.mode,
      totalGames: data.totalGames,
      firstSide: data.firstSide,
      fearlessEnabled: data.fearlessEnabled,
      games: data.games,
      blueName: currentBlueName,
      redName: currentRedName,
      winners: winnersArr,
    });
    archive.addArchive(arc);
    // 友好提示
    try {
      window.alert(`已保存到档案：${arc.label}\n（共 ${data.games.length}/${data.totalGames} 局）`);
    } catch (_) {}
  }, [canSaveCurrentBO, bo, currentWinners, currentBlueName, currentRedName, archive]);

  // BO 系列完成时自动弹保存提示（每个 BO 只弹一次）
  const promptedSaveForRef = useRef(null);
  useEffect(() => {
    if (!bo.isSeriesComplete) return;
    if (!canSaveCurrentBO) return;
    // 用 totalGames + completedGames 数量做"指纹"，避免同一 BO 反复弹窗
    const fp = `${bo.mode}_${bo.totalGames}_${bo.completedGames.length}`;
    if (promptedSaveForRef.current === fp) return;
    promptedSaveForRef.current = fp;
    // 异步弹，避免阻塞 React 渲染
    setTimeout(() => {
      try {
        if (window.confirm(
          `BO 系列已完成（${bo.completedGames.length}/${bo.totalGames}）！\n是否立即保存到档案？\n（可稍后在「档案」抽屉里补填胜负 / 队名）`
        )) {
          handleSaveCurrentBO();
        }
      } catch (_) {}
    }, 300);
  }, [bo.isSeriesComplete, bo.mode, bo.totalGames, bo.completedGames.length, canSaveCurrentBO, handleSaveCurrentBO]);

  // 把存档加载回主界面：覆盖当前 BO + 重置 draft & timer
  const handleLoadArchive = useCallback((arc) => {
    if (!arc) return;
    const resolveChampion = (slim) => {
      if (!slim) return null;
      const full = champions.find((c) =>
        (c.id && slim.id && String(c.id) === String(slim.id)) ||
        (c.idName && slim.idName && c.idName === slim.idName)
      );
      return full || slim; // fallback：用 slim 自己（含 squareImg）
    };
    bo.loadFromArchive(arc, resolveChampion);
    draft.reset();
    timer.resetTimer();
    // 把存档里的队名 / winners 回灌到 live state
    setCurrentBlueName(arc.blueName || '蓝方');
    setCurrentRedName(arc.redName || '红方');
    const w = {};
    (arc.games || []).forEach((g) => { if (g.winner) w[g.gameIndex] = g.winner; });
    setCurrentWinners(w);
  }, [bo, draft, timer, champions]);

  // ========== 复盘视图派生：effectiveDraft + effectiveBO ==========
  // - 不在复盘 → 跟真 draft / 真 bo 一样
  // - 复盘 → 用 replaySnapshot 构造"假 draft"，bo 也修一下让显示的局号/先手对得上
  //   这样 Overlay、ExportCard、buildExportJson 全部自动跟着复盘切换，
  //   避免"导出却拿到当前局"的 bug
  const effectiveDraft = useMemo(() => {
    if (!bo.isReplayMode || !bo.replaySnapshot) return draft;
    const snap = bo.replaySnapshot;
    const buildTeam = (picks, bans) => ({
      picks: Array.from({ length: 5 }, (_, i) => ({
        champion: (picks && picks[i]) || null,
        isActive: false,
      })),
      bans: Array.from({ length: 5 }, (_, i) => ({
        champion: (bans && bans[i]) || null,
        isActive: false,
      })),
    });
    return {
      blueTeam:   buildTeam(snap.bluePicks, snap.blueBans),
      redTeam:    buildTeam(snap.redPicks,  snap.redBans),
      phaseName:  `复盘 Game ${bo.viewingGameIndex + 1}`,
      isFinished: true,
      stepIndex:  0,
      currentStep: null,
      usedChampionIds: new Set(),
      snapshotPicks: () => ({
        bluePicks: (snap.bluePicks || []).filter(Boolean),
        redPicks:  (snap.redPicks  || []).filter(Boolean),
        blueBans:  (snap.blueBans  || []).filter(Boolean),
        redBans:   (snap.redBans   || []).filter(Boolean),
      }),
      swapSelection: null,
      swapEnabled:   false,
      // 复盘视图下，draft 的写操作全部 no-op
      pickChampion:     () => {},
      undo:             () => {},
      reset:            () => {},
      clickPickForSwap: () => {},
    };
  }, [bo.isReplayMode, bo.replaySnapshot, bo.viewingGameIndex, draft]);

  const effectiveBO = useMemo(() => {
    if (!bo.isReplayMode) return bo;
    return {
      ...bo,
      currentGameIndex:  bo.viewingGameIndex,
      currentGameNumber: bo.viewingGameIndex + 1,
      currentFirstSide:  (bo.replaySnapshot && bo.replaySnapshot.firstSide) || bo.currentFirstSide,
    };
  }, [bo]);

  // 组装 Overlay 期望的 state 形状（统一吃 effectiveDraft）
  const overlayState = useMemo(() => {
    const raw = {
      champSelectActive: true,
      leagueConnected: true,
      state: effectiveDraft.phaseName,
      timer: effectiveDraft.isFinished ? 0 : timer.secondsLeft,
      blueTeam: effectiveDraft.blueTeam,
      redTeam: effectiveDraft.redTeam,
    };
    return convertState(raw, '');
  }, [
    effectiveDraft.blueTeam, effectiveDraft.redTeam,
    effectiveDraft.phaseName, effectiveDraft.isFinished,
    timer.secondsLeft,
  ]);

  const draftStarted = draft.stepIndex > 0 && !draft.isFinished;

  // ========== 一键导出战术板（PNG + JSON）==========
  // exportCardRef 指向离屏渲染的 <ExportCard>，html2canvas 只拍这张卡，
  // 截出来就是一张干净的复盘图（不含英雄选择网格、不含控制台按钮）
  const exportCardRef = useRef(null);

  const buildExportJson = useCallback(() => {
    const slim = (c) => (c && c.id
      ? { id: c.id, idName: c.idName || String(c.id), name: c.name || '' }
      : null);
    const snap = effectiveDraft.snapshotPicks();
    // 复盘时导出"复盘的这一局"，否则导出当前正在打的局
    const exportedGameNumber = bo.isReplayMode
      ? (bo.viewingGameIndex + 1)
      : bo.currentGameNumber;
    const exportedFirstSide = bo.isReplayMode && bo.replaySnapshot
      ? bo.replaySnapshot.firstSide
      : bo.currentFirstSide;

    return {
      exportedAt: new Date().toISOString(),
      schemaVersion: 2,
      exportContext: bo.isReplayMode
        ? { mode: 'replay', viewingGameNumber: exportedGameNumber }
        : { mode: 'live' },
      series: {
        mode: bo.mode,
        totalGames: bo.totalGames,
        currentGameNumber: bo.currentGameNumber,
        isSeriesComplete: bo.isSeriesComplete,
        fearlessEnabled: bo.fearlessEnabled,
      },
      currentGame: {
        gameNumber: exportedGameNumber,
        firstSide: exportedFirstSide,
        nextGameFirstSide: bo.nextFirstSide,
        stepIndex: effectiveDraft.stepIndex,
        phaseName: effectiveDraft.phaseName,
        isFinished: effectiveDraft.isFinished,
        blueTeam: {
          picks: snap.bluePicks.map(slim),
          bans: snap.blueBans.map(slim),
        },
        redTeam: {
          picks: snap.redPicks.map(slim),
          bans: snap.redBans.map(slim),
        },
      },
      completedGames: bo.completedGames.map((g) => ({
        gameNumber: g.gameIndex + 1,
        firstSide: g.firstSide,
        blueTeam: {
          picks: (g.bluePicks || []).map(slim),
          bans: (g.blueBans || []).map(slim),
        },
        redTeam: {
          picks: (g.redPicks || []).map(slim),
          bans: (g.redBans || []).map(slim),
        },
      })),
      globalLocks: Array.from(bo.globalLockedMap.entries()).map(([id, info]) => ({
        championId: id,
        championName: info.championName,
        lockedFromGame: info.gameNumber,
        lockedBy: info.side,
      })),
    };
  }, [bo, effectiveDraft]);

  const exportTactic = useExportTactic({
    getCaptureNode: () => exportCardRef.current,
    buildJson: buildExportJson,
  });

  // ========== 复盘编辑模式（启用后可 swap 或替换历史局的 pick/ban）==========
  // - replayEditing：开关；退出复盘自动关闭
  // - replayEditSel：当前等待替换的目标 { team, kind: 'pick'|'ban', slot, championId? }
  // - replaySwapSel：摇摆模式下当前选中的 pick { team, slot }
  const [replayEditing, setReplayEditing] = useState(false);
  const [replayEditSel, setReplayEditSel] = useState(null);
  const [replaySwapSel, setReplaySwapSel] = useState(null);

  // 离开复盘 / 切局 / 切 BO 模式 时自动重置编辑状态
  useEffect(() => {
    if (!bo.isReplayMode) {
      setReplayEditing(false);
      setReplayEditSel(null);
      setReplaySwapSel(null);
    } else {
      // 切换查看的局也清除等待
      setReplayEditSel(null);
      setReplaySwapSel(null);
    }
  }, [bo.isReplayMode, bo.viewingGameIndex]);

  const toggleReplayEdit = useCallback(() => {
    setReplayEditing((v) => !v);
    setReplayEditSel(null);
    setReplaySwapSel(null);
  }, []);

  // 复盘 + 编辑模式下：点击 Overlay 的 Pick 时的行为
  //   - 已选中 swap → 同队互换；非同队或同位 → 重选起点
  //   - 否则 → 标记为 swap 起点
  // 点击 ChampionGrid 的英雄时的行为：
  //   - 若有 replayEditSel（等待替换） → 把那个 slot 的英雄替换成点击的英雄
  //   - 否则忽略
  const handleReplayPickClick = useCallback((team, slot) => {
    if (!bo.isReplayMode || !replayEditing || !bo.replaySnapshot) return;
    // 取这条 pick 是否有英雄
    const sidePicks = team === 'blue' ? bo.replaySnapshot.bluePicks : bo.replaySnapshot.redPicks;
    const cur = sidePicks && sidePicks[slot];
    if (!cur) return; // 空位用替换模式（下方逻辑）

    // 如果当前有 swap 起点
    if (replaySwapSel) {
      if (replaySwapSel.team !== team || replaySwapSel.slot === slot) {
        // 不同队或同位 → 重置为新起点
        setReplaySwapSel({ team, slot });
        return;
      }
      // 同队互换
      const arr = sidePicks.slice();
      const tmp = arr[replaySwapSel.slot];
      arr[replaySwapSel.slot] = arr[slot];
      arr[slot] = tmp;
      const patch = team === 'blue' ? { bluePicks: arr } : { redPicks: arr };
      bo.updateGameSnapshot(bo.viewingGameIndex, patch);
      setReplaySwapSel(null);
      return;
    }
    setReplaySwapSel({ team, slot });
  }, [bo, replayEditing, replaySwapSel]);

  // 点击 ChampionGrid 的英雄（仅在 editing + 有 replayEditSel 时生效，用来替换 slot）
  const handleReplayReplaceFromGrid = useCallback((champion) => {
    if (!bo.isReplayMode || !replayEditing || !replayEditSel || !bo.replaySnapshot) return;
    const { team, kind, slot } = replayEditSel;
    const snap = bo.replaySnapshot;
    const sideArr = (
      kind === 'pick'
        ? (team === 'blue' ? snap.bluePicks : snap.redPicks)
        : (team === 'blue' ? snap.blueBans  : snap.redBans)
    ) || [];
    const arr = sideArr.slice();
    while (arr.length <= slot) arr.push(null);
    arr[slot] = champion;
    const key =
      kind === 'pick'
        ? (team === 'blue' ? 'bluePicks' : 'redPicks')
        : (team === 'blue' ? 'blueBans'  : 'redBans');
    bo.updateGameSnapshot(bo.viewingGameIndex, { [key]: arr });
    setReplayEditSel(null);
  }, [bo, replayEditing, replayEditSel]);

  // 由 Pick / Ban 组件触发"进入替换等待"（在 Overlay 里点 slot 进入 replace 模式）
  // 这里我们用一个外部接口：用户对 swap 友好型 UX，所以
  //   - 双击 Pick → 切到"等待替换"
  //   - 单击 Pick → swap 起点
  // 简单起见，先暴露 handleReplayBeginReplace(team, kind, slot)，
  // Overlay 端会用右键 / 长按触发；UI 上加一个"替换"小按钮也能用
  const handleReplayBeginReplace = useCallback((team, kind, slot) => {
    if (!bo.isReplayMode || !replayEditing) return;
    setReplaySwapSel(null);
    setReplayEditSel({ team, kind, slot });
  }, [bo.isReplayMode, replayEditing]);

  const cancelReplayEdit = useCallback(() => {
    setReplaySwapSel(null);
    setReplayEditSel(null);
  }, []);

  // ========== AI 系统（青铜套餐 5 大本地推荐）==========
  // - aiSettings: 持久化的总开关 + 各功能 toggle（写入 localStorage）
  // - aiPanelOpen: 控制设置面板的弹出
  // - insights: 把"实时 BP + 赛事数据"喂给 5 个青铜函数，
  //             aiSettings.isAIOn = false 时全部返回 null（0 算力开销）
  const aiSettings = useAISettings();
  const [aiPanelOpen, setAiPanelOpen] = useState(false);

  const insights = useAIInsights({
    aiSettings,
    draft,
    champions,
    tournamentData,
  });

  return (
    <div className={css.CoachRoot}>
      {/* 离屏战术板：用户看不见，html2canvas 截它就得到一张干净的复盘图。
          复盘时 effectiveBO/effectiveDraft 会指向那一历史局，导出的就是那一局。 */}
      <div className={css.ExportStage} aria-hidden="true">
        <ExportCard
          ref={exportCardRef}
          bo={effectiveBO}
          draft={effectiveDraft}
          exportedAtFormatted={formatNowDateTime()}
        />
      </div>

      <BOControlPanel
        mode={bo.mode}
        totalGames={bo.totalGames}
        currentGameIndex={bo.currentGameIndex}
        currentGameNumber={bo.currentGameNumber}
        currentFirstSide={bo.currentFirstSide}
        nextFirstSide={bo.nextFirstSide}
        fearlessEnabled={bo.fearlessEnabled}
        isSeriesComplete={bo.isSeriesComplete}
        completedGames={bo.completedGames}
        viewingGameIndex={bo.viewingGameIndex}
        isReplayMode={bo.isReplayMode}
        replayEditing={replayEditing}
        onToggleReplayEdit={toggleReplayEdit}
        onViewGame={bo.setViewingGame}
        onReturnToCurrent={bo.returnToCurrent}
        onRollbackToGame={(idx) => {
          bo.rollbackToGame(idx);
          draft.reset();
          timer.resetTimer();
        }}
        draftStarted={draftStarted}
        draftFinished={draft.isFinished}
        isPaused={timer.isPaused}
        onChangeMode={onChangeMode}
        onToggleFearless={bo.toggleFearless}
        onToggleCurrentFirstSide={bo.toggleCurrentFirstSide}
        onToggleNextFirstSide={bo.toggleNextFirstSide}
        onTogglePause={timer.togglePause}
        onNextGame={onNextGame}
        onResetBO={onResetBO}
        onExport={exportTactic.exportNow}
        exporting={exportTactic.exporting}
        onOpenArchive={() => setArchivePanelOpen(true)}
        onSaveCurrentBO={handleSaveCurrentBO}
        canSaveCurrentBO={canSaveCurrentBO}
        archiveCount={archive.archives.length}
        onOpenAI={() => setAiPanelOpen(true)}
        aiOn={aiSettings.isAIOn}
        dataSources={tournamentData.sources}
        selectedSourceIds={tournamentData.selectedSourceIds}
        onToggleSource={tournamentData.toggleSource}
        onSelectOnly={tournamentData.selectOnly}
        onSelectByLeague={tournamentData.selectByLeague}
        currentMeta={tournamentData.currentMeta}
        dataLoading={tournamentData.loading}
      />

      {/* 复盘模式横幅 + 编辑工具 */}
      {bo.isReplayMode && (
        <div
          className={cx(css.ReplayBanner, replayEditing && css.ReplayBannerEditing)}
          role="status"
        >
          <span className={css.ReplayBannerIcon} aria-hidden="true">
            {replayEditing ? '✎' : '▶'}
          </span>
          {!replayEditing ? (
            <span className={css.ReplayBannerText}>
              复盘模式 · 正在查看 Game {bo.viewingGameIndex + 1} 的历史 BP（只读）
            </span>
          ) : !replayEditSel ? (
            <span className={css.ReplayBannerText}>
              编辑 Game {bo.viewingGameIndex + 1} · 单击 Pick 选起点 → 再点同队另一 Pick 互换 ·
              或在下拉里选「替换位置」
            </span>
          ) : (
            <span className={css.ReplayBannerText}>
              已选择替换位置：
              <strong className={css[`ReplayBannerSel_${replayEditSel.team}`]}>
                {replayEditSel.team === 'blue' ? '蓝方' : '红方'}{' '}
                {replayEditSel.kind === 'pick' ? 'Pick' : 'Ban'}
                #{replayEditSel.slot + 1}
              </strong>
              {' →  '}
              <em>请到右侧英雄网格点一个英雄完成替换</em>
            </span>
          )}

          {/* 编辑模式下：替换位置下拉框 */}
          {replayEditing && (
            <select
              className={css.ReplayBannerSelect}
              value={
                replayEditSel
                  ? `${replayEditSel.team}-${replayEditSel.kind}-${replayEditSel.slot}`
                  : ''
              }
              onChange={(e) => {
                if (!e.target.value) {
                  cancelReplayEdit();
                  return;
                }
                const [team, kind, slotStr] = e.target.value.split('-');
                handleReplayBeginReplace(team, kind, Number(slotStr));
              }}
              title="选择要替换的位置 → 然后在网格里点英雄"
            >
              <option value="">替换位置…</option>
              <optgroup label="蓝方 Pick">
                {[0,1,2,3,4].map((i) => (
                  <option key={`bp${i}`} value={`blue-pick-${i}`}>蓝 Pick {i + 1}</option>
                ))}
              </optgroup>
              <optgroup label="蓝方 Ban">
                {[0,1,2,3,4].map((i) => (
                  <option key={`bb${i}`} value={`blue-ban-${i}`}>蓝 Ban {i + 1}</option>
                ))}
              </optgroup>
              <optgroup label="红方 Pick">
                {[0,1,2,3,4].map((i) => (
                  <option key={`rp${i}`} value={`red-pick-${i}`}>红 Pick {i + 1}</option>
                ))}
              </optgroup>
              <optgroup label="红方 Ban">
                {[0,1,2,3,4].map((i) => (
                  <option key={`rb${i}`} value={`red-ban-${i}`}>红 Ban {i + 1}</option>
                ))}
              </optgroup>
            </select>
          )}

          {replayEditing && replayEditSel && (
            <button
              type="button"
              className={css.ReplayBannerCancel}
              onClick={cancelReplayEdit}
              title="取消等待替换"
            >
              取消
            </button>
          )}

          <button
            type="button"
            className={css.ReplayBannerBtn}
            onClick={bo.returnToCurrent}
          >
            返回 Game {bo.currentGameNumber}
          </button>
        </div>
      )}

      <ChampionGrid
        champions={champions}
        loading={loading}
        error={error}
        usedChampionIds={
          // 编辑替换时：用历史局已选过的英雄集合，避免选到本局已用的
          (bo.isReplayMode && replayEditing && replayEditSel && bo.replaySnapshot)
            ? (() => {
                const s = new Set();
                ['bluePicks','redPicks','blueBans','redBans'].forEach((k) => {
                  (bo.replaySnapshot[k] || []).forEach((c) => c && c.id && s.add(c.id));
                });
                return s;
              })()
            : draft.usedChampionIds
        }
        globalLockedMap={bo.globalLockedMap}
        currentStep={
          // 编辑替换等待时，让 ChampionGrid 顶部显示当前等待替换的目标
          (bo.isReplayMode && replayEditing && replayEditSel)
            ? {
                team: replayEditSel.team,
                type: replayEditSel.kind, // 'pick' | 'ban'
                slot: replayEditSel.slot,
              }
            : draft.currentStep
        }
        // 编辑替换等待时，关掉 isFinished 让网格可交互；其它复盘场景仍然冻结
        isFinished={
          (bo.isReplayMode && replayEditing && replayEditSel)
            ? false
            : (draft.isFinished || bo.isSeriesComplete || bo.isReplayMode)
        }
        onPick={
          (bo.isReplayMode && replayEditing && replayEditSel)
            ? handleReplayReplaceFromGrid
            : (bo.isReplayMode ? () => {} : draft.pickChampion)
        }
        onUndo={
          (bo.isReplayMode && replayEditing && replayEditSel)
            ? cancelReplayEdit                // 撤销变成"取消等待替换"
            : (bo.isReplayMode ? () => {} : draft.undo)
        }
        onReset={() => {
          if (bo.isReplayMode) {
            if (replayEditing) cancelReplayEdit();
            return;
          }
          draft.reset();
          timer.resetTimer();
        }}
        tournamentData={tournamentData}
      />

      <Overlay
        state={overlayState}
        config={COACH_CONFIG}
        onPickClick={
          (bo.isReplayMode && replayEditing)
            ? handleReplayPickClick
            : (bo.isReplayMode ? () => {} : draft.clickPickForSwap)
        }
        swapSelection={
          (bo.isReplayMode && replayEditing)
            ? replaySwapSel
            : (bo.isReplayMode ? null : draft.swapSelection)
        }
        swapEnabled={
          (bo.isReplayMode && replayEditing)
            ? true                                // 编辑模式始终允许 swap
            : (bo.isReplayMode ? false : draft.swapEnabled)
        }
      />

      {/* AI 推荐抽屉：贴右侧，总开关关闭时自动隐藏 */}
      <AIInsightPanel insights={insights} aiSettings={aiSettings} />

      {/* AI 设置模态面板：点击 BO 控制台齿轮按钮打开 */}
      {aiPanelOpen && (
        <AISettingsPanel
          aiSettings={aiSettings}
          onClose={() => setAiPanelOpen(false)}
        />
      )}

      {/* BO 档案抽屉：localStorage 历史 BO 管理 + 系列总结导出 */}
      <BOArchivePanel
        open={archivePanelOpen}
        onClose={() => setArchivePanelOpen(false)}
        archives={archive.archives}
        onRename={archive.renameArchive}
        onRemove={archive.removeArchive}
        onSetGameWinner={archive.setGameWinner}
        onDownloadJson={archive.downloadArchive}
        onDownloadAll={archive.downloadAll}
        onImportFile={archive.importFromFile}
        onLoadToMain={handleLoadArchive}
      />
    </div>
  );
};

// 'YYYY-MM-DD HH:mm'：导出卡右上角时间戳。每次渲染都重新算，
// 用户点导出那一刻渲染的就是当时的时间。
function formatNowDateTime() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

export default CoachApp;
